/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Array expansion", description = "Expand array columns into individual sync rows so each array element is synced as a separate record to your destination.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Array expansion",
        description: "Expand array columns into individual sync rows so each array element is synced as a separate record to your destination.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How it works",
          "depth": 0,
          "id": "how-it-works"
        }, {
          "value": "Example",
          "depth": 1,
          "id": "example"
        }, {
          "value": "Empty arrays",
          "depth": 1,
          "id": "empty-arrays"
        }, {
          "value": "Duplicate elements",
          "depth": 1,
          "id": "duplicate-elements"
        }, {
          "value": "Configuration",
          "depth": 0,
          "id": "configuration"
        }, {
          "value": "Supported column types",
          "depth": 0,
          "id": "supported-column-types"
        }, {
          "value": "Limitations",
          "depth": 0,
          "id": "limitations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      h3: "h3",
      code: "code",
      ol: "ol",
      li: "li",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Array expansion syncs each element of an array column as its own record, so one source row can become many destination records."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams syncing columns that hold arrays of values"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.a, {
                href: "/syncs/lightning-sync-engine",
                children: "Lightning sync engine"
              }), " on a supported source."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use array expansion when your source stores multiple values in a single array column but your destination expects one record per value. For example, if a user has several device IDs in an array column, array expansion creates a separate sync row for each ID so you can sync each one individually."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Array expansion is supported with the ", _jsx(_components.strong, {
            children: "Lightning sync engine"
          }), " on\n", _jsx(_components.strong, {
            children: "Postgres"
          }), ", ", _jsx(_components.strong, {
            children: "Snowflake"
          }), ", ", _jsx(_components.strong, {
            children: "BigQuery"
          }), ", and ", _jsx(_components.strong, {
            children: "Databricks"
          }), " sources."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "When you map an array-type column as the identifier in your sync configuration, Hightouch automatically enables array expansion. Each element of the array becomes a separate row, and a generated column is used as the identifier value sent to the destination."
      }), "\n", _jsx(_components.h3, {
        id: "example",
        children: "Example"
      }), "\n", _jsxs(_components.p, {
        children: ["Consider this source table with an array column ", _jsx(_components.code, {
          children: "device_ids"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "user_id"
            }), _jsx(_components.th, {
              children: "name"
            }), _jsx(_components.th, {
              children: "device_ids"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: "Alice"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[\"ios_a1\", \"android_b2\"]"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: "Bob"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[\"ios_c3\"]"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: "Charlie"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[]"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "4"
            }), _jsx(_components.td, {
              children: "Dana"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "[\"web_d4\", \"web_d4\"]"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["When you map ", _jsx(_components.code, {
          children: "device_ids"
        }), " as the identifier for your sync, Hightouch expands each row so that every array element becomes its own record:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "_ht_device_ids_element"
            }), _jsx(_components.th, {
              children: "user_id"
            }), _jsx(_components.th, {
              children: "name"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ios_a1"
              })
            }), _jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: "Alice"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "android_b2"
              })
            }), _jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: "Alice"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ios_c3"
              })
            }), _jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: "Bob"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "web_d4"
              })
            }), _jsx(_components.td, {
              children: "4"
            }), _jsx(_components.td, {
              children: "Dana"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The generated column ", _jsx(_components.code, {
          children: "_ht_device_ids_element"
        }), " contains the individual element value and is used as the identifier sent to the destination."]
      }), "\n", _jsx(_components.h3, {
        id: "empty-arrays",
        children: "Empty arrays"
      }), "\n", _jsxs(_components.p, {
        children: ["Rows where the array column is empty (", _jsx(_components.code, {
          children: "[]"
        }), ") or ", _jsx(_components.code, {
          children: "NULL"
        }), " produce ", _jsx(_components.strong, {
          children: "zero output rows"
        }), ". In the example above, Charlie's row is omitted entirely because ", _jsx(_components.code, {
          children: "device_ids"
        }), " is an empty array."]
      }), "\n", _jsx(_components.h3, {
        id: "duplicate-elements",
        children: "Duplicate elements"
      }), "\n", _jsxs(_components.p, {
        children: ["If an array contains duplicate values (like Dana's two ", _jsx(_components.code, {
          children: "\"web_d4\""
        }), " entries), the duplicates produce a ", _jsx(_components.strong, {
          children: "primary key collision"
        }), ". The sync completes with a \"Report\" status and the duplicate rows are flagged as rejected. Only one row per unique element value is synced."]
      }), "\n", _jsx(_components.h2, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Array expansion is configured automatically when you map an array-type column as the identifier in the ", _jsx(_components.strong, {
          children: "\"How should rows be matched?\""
        }), " section of your sync configuration."]
      }), "\n", _jsx(_components.p, {
        children: "When an array column is selected:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch sets the ", _jsx(_components.code, {
            children: "arrayExpansionSourceColumn"
          }), " in your sync config"]
        }), "\n", _jsxs(_components.li, {
          children: ["The identifier mapping is automatically translated to use the generated element column (", _jsx(_components.code, {
            children: "_ht_{column}_element"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: "An info banner confirms that array expansion is active"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To disable array expansion, change the identifier mapping to a non-array column."
      }), "\n", _jsx(_components.h2, {
        id: "supported-column-types",
        children: "Supported column types"
      }), "\n", _jsx(_components.p, {
        children: "Array expansion works with columns of the following types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Native arrays"
          }), ": Postgres ", _jsx(_components.code, {
            children: "text[]"
          }), ", Snowflake ", _jsx(_components.code, {
            children: "ARRAY"
          }), ", BigQuery ", _jsx(_components.code, {
            children: "ARRAY<STRING>"
          }), ", Databricks ", _jsx(_components.code, {
            children: "ARRAY<STRING>"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "JSON arrays"
          }), ": Postgres ", _jsx(_components.code, {
            children: "jsonb"
          }), ", Snowflake ", _jsx(_components.code, {
            children: "VARIANT"
          }), ", BigQuery ", _jsx(_components.code, {
            children: "JSON"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "JSON string arrays"
          }), ": ", _jsx(_components.code, {
            children: "VARCHAR"
          }), " columns containing JSON-encoded arrays like ", _jsx(_components.code, {
            children: "'[\"a\",\"b\"]'"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch automatically detects the column type and applies the appropriate expansion strategy."
      }), "\n", _jsx(_components.h2, {
        id: "limitations",
        children: "Limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Only ", _jsx(_components.strong, {
            children: "one array column"
          }), " can be expanded per sync"]
        }), "\n", _jsxs(_components.li, {
          children: ["Array expansion requires the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: _jsx(_components.strong, {
              children: "Lightning sync engine"
            })
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Only supported on ", _jsx(_components.strong, {
            children: "Postgres"
          }), ", ", _jsx(_components.strong, {
            children: "Snowflake"
          }), ", ", _jsx(_components.strong, {
            children: "BigQuery"
          }), ", and ", _jsx(_components.strong, {
            children: "Databricks"
          }), " sources"]
        }), "\n", _jsx(_components.li, {
          children: "Duplicate values within an array cause row-level rejections"
        }), "\n", _jsxs(_components.li, {
          children: ["Array expansion is not available on syncs that use ", _jsx(_components.a, {
            href: "/match-booster/overview",
            children: "Match Booster"
          }), ", as Match Booster applies its own identity resolution logic that is incompatible with row expansion"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
